import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { BadgeRow } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";
import { toast } from "sonner";
import { Star, MapPin, ArrowLeft, Wrench, MessageSquare, CalendarClock, Image as ImageIcon } from "lucide-react";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="mt-6" data-testid={testid}>
    <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 flex items-center gap-1"><Icon size={12}/> {title}</h3>
    <div className="mt-3">{children}</div>
  </div>
);

const GAP_NOTES = { vetted: "Vetting in progress", bonded: "Bonding verification in progress", insured: "Insurance verification in progress" };

const BadgeGaps = ({ badges }) => {
  const missing = Object.keys(GAP_NOTES).filter((k) => !badges?.[k]);
  if (!missing.length) return null;
  return (
    <div className="flex flex-col items-start sm:items-end gap-0.5" data-testid="badge-gap-notes">
      {missing.map((k) => <span key={k} data-testid={`badge-gap-${k}`} className="text-xs text-slate-400">{GAP_NOTES[k]}</span>)}
    </div>
  );
};

const Chips = ({ items, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {items.map((t) => (
      <span key={t} className="text-sm px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">{t}</span>
    ))}
  </div>
);

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [aiSum, setAiSum] = useState(null);
  const [aiSumBusy, setAiSumBusy] = useState(false);
  const [msgBusy, setMsgBusy] = useState(false);
  const { user } = useAuth();
  const messageBusiness = async () => {
    if (!user) { navigate("/login"); return; }
    setMsgBusy(true);
    try { const r = await api.post("/threads", { business_id: id }); navigate(`/messages/${r.data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start a conversation"); }
    finally { setMsgBusy(false); }
  };
  const summarize = async () => {
    setAiSumBusy(true);
    try { const r = await api.post(`/ai/review-summary/${id}`); setAiSum(r.data); }
    catch { setAiSum(null); }
    finally { setAiSumBusy(false); }
  };

  useEffect(() => { api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => {}); }, [id]);

  usePageMeta(b?.business_name || "Business profile",
    b ? `${b.business_name} — ${(b.services || b.trades || []).join(", ")} in ${(b.service_areas || []).join(", ")}. A Black-owned business on Black Umbrella.` : "A vetted Black-owned business on Black Umbrella.");

  if (!b) return <div className="max-w-4xl mx-auto px-4 py-16 text-slate-400">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-950">{b.business_name}</h1>
            <div className="flex items-center gap-1 mt-2 text-amber-600 font-semibold">
              <Star size={16} className="fill-amber-500 text-amber-500" />
              {b.rating > 0 ? b.rating : "New"} <span className="text-slate-400 font-normal">· {b.review_count} reviews</span>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
              <MapPin size={14} /> {(b.service_areas || []).join(", ")}
            </div>
            {b.years_in_business && (
              <div className="flex items-center gap-1 mt-2 text-sm text-slate-500" data-testid="business-years">
                <CalendarClock size={14} /> {b.years_in_business} years in business
              </div>
            )}
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1.5">
            <BadgeRow badges={b.badges} />
            <BadgeGaps badges={b.badges} />
          </div>
        </div>

        <div className="mt-6" data-testid="business-about">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">About</h3>
          <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line">{b.about || "This vetted Black-owned business serves clients across its coverage area."}</p>
        </div>

        <Section icon={Wrench} title="Services" testid="business-services">
          <Chips items={b.services || b.trades || []} testid="business-services-list" />
        </Section>

        <Section icon={MapPin} title="Service areas" testid="business-areas">
          <Chips items={b.service_areas || []} testid="business-areas-list" />
        </Section>

        {b.gallery?.length > 0 && (
          <Section icon={ImageIcon} title="Portfolio" testid="business-gallery">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {b.gallery.map((fid) => (
                <img key={fid} data-testid={`gallery-photo-${fid}`} src={`${API}/businesses/${id}/gallery/${fid}`} alt="Portfolio work"
                  className="aspect-square w-full object-cover rounded-xl border border-slate-200 bg-slate-100" loading="lazy" />
              ))}
            </div>
          </Section>
        )}

        {b.insurance_expiry && (
          <p className="mt-4 text-xs text-blue-700 bg-blue-50 inline-block px-3 py-1.5 rounded-full">
            Insurance verified · valid through {b.insurance_expiry}
          </p>
        )}

        <Button data-testid="request-quote-button" onClick={() => navigate(`/post-job?trade=${encodeURIComponent(b.trades?.[0] || "")}`)}
          className="mt-8 h-12 px-6 rounded-full bg-slate-950 hover:bg-amber-600 text-white font-semibold">
          Post a job to get a quote
        </Button>
        <Button data-testid="message-business-button" variant="outline" onClick={messageBusiness} disabled={msgBusy}
          className="mt-8 ml-0 sm:ml-3 h-12 px-6 rounded-full border-slate-300 font-semibold">
          <MessageSquare size={16} className="mr-2" /> {msgBusy ? "Opening…" : "Message business"}
        </Button>
      </div>

      <div className="mt-8" data-testid="business-reviews">
        <h2 className="font-heading text-2xl font-bold text-slate-950">Reviews{b.reviews?.length ? ` (${b.reviews.length})` : ""}</h2>
        {!b.reviews?.length && <p className="mt-2 text-sm text-slate-400" data-testid="no-reviews">No written reviews yet.</p>}
      </div>

      {b.reviews?.length > 0 && (
        <div className="mt-1">
          <button type="button" data-testid="ai-review-summary-btn" onClick={summarize} disabled={aiSumBusy} className="mt-1 text-sm font-semibold text-amber-700 hover:underline">{aiSumBusy ? "Summarizing…" : "✨ Summarize reviews with AI"}</button>
          {aiSum?.summary && (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm" data-testid="ai-review-summary">
              <p className="text-slate-800">{aiSum.summary}</p>
              {aiSum.pros?.length > 0 && <p className="mt-2 text-emerald-700"><strong>Pros:</strong> {aiSum.pros.join(", ")}</p>}
              {aiSum.cons?.length > 0 && <p className="mt-1 text-slate-600"><strong>Watch:</strong> {aiSum.cons.join(", ")}</p>}
            </div>
          )}
          <div className="mt-4 space-y-4">
            {b.reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-slate-200 p-5 bg-white">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{r.client_name}{r.created_at && <span className="ml-2 text-xs font-normal text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>}</span>
                  <div className="flex items-center gap-0.5 text-amber-500">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.id}-star-${i}`} size={14} className="fill-amber-500" />)}
                  </div>
                </div>
                <p className="mt-2 text-slate-600 text-sm">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
